import { useEffect, useMemo, useState } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { supabase } from '../../lib/supabase'
import { storage } from '../../lib/storage'
import { fonts } from '../../lib/theme'
import { useAuth } from '../../lib/AuthContext'
import { useThemeColors } from '../../lib/useThemeColors'
import { resolvePerson } from '../../lib/personAvatar'
import { useSpecialJersey } from '../../lib/useSpecialJerseys'
import PersonAvatar from '../PersonAvatar'
import ShowcaseRow from '../Badges/ShowcaseRow'
import { useShowcase } from '../../lib/useShowcase'
import { legibleOn } from '../../lib/contrast'

const HEX = /^#[0-9A-Fa-f]{6}$/

// ─── WelcomeHeader ───────────────────────────────────────────────────
// Riga compatta (come VB): avatar ritagliato (testa e spalle), saluto e stato. Il default e'
// generato dall'id utente finche' non lo personalizza. Toccando l'avatar si va al Profilo.
// Nei grandi giri chi ha una maglia leader (GC, punti, GPM) la indossa: colore + nome gara nel chip.
export function WelcomeHeader({ nickname, picks }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const router = useRouter()
  const { user, avatarSettings, leagueId } = useAuth()
  const [jersey, setJersey] = useState(null)
  const showcase = useShowcase(leagueId, user?.id)   // i 3 badge in evidenza, di fianco all'avatar
  const special = useSpecialJersey(user?.id)   // maglia speciale di lega (campione del mondo): cede il posto alla maglia di un giro in corso

  useEffect(() => {
    if (!user?.id) return
    let alive = true
    supabase.rpc('fn_user_jersey', { p_id_user: user.id, p_id_league: leagueId }).single()
      .then(({ data }) => { if (alive) setJersey(data?.maglia ? data : null) })
    return () => { alive = false }
  }, [user?.id, leagueId])

  const person = useMemo(() => {
    const p = resolvePerson(avatarSettings, user?.id)
    // maglia di default nel colore della competizione, finche' l'utente non sceglie la sua
    if (!avatarSettings?.person?.main) { p.main = tc.rosa; p.accent = tc.rosa }
    if (jersey?.color && HEX.test(jersey.color)) {
      if (jersey.maglia === 'gm' && jersey.gm_pattern === 'dots') {
        // maglia a pois (Tour, Vuelta): base bianca, pois e colletto nel colore GPM
        p.jersey = 5; p.main = '#FFFFFF'; p.accent = jersey.color; p.trim = jersey.color
      } else {
        p.main = jersey.color; p.accent = jersey.color; p.trim = jersey.color   // tinta unita, colletto compreso
      }
    }
    return p
  }, [avatarSettings, user?.id, tc.rosa, jersey])

  const jerseyLabel = jersey?.maglia === 'gc' ? t('home.jersey_gc')
    : jersey?.maglia === 'pt' ? t('home.jersey_pt')
      : jersey?.maglia === 'gm' ? t('home.jersey_gm')
        : null

  let status = null
  if (picks?.length) status = picks.every(p => p.has_picked) ? t('home.status_ready') : t('home.status_todo')

  return (
    <View style={s.row}>
      <TouchableOpacity
        onPress={() => router.push('/profilo')}
        style={[s.avatarBox, special && !jersey && s.avatarBoxSpecial]}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={t('avatar.edit_a11y')}
      >
        <View style={s.avatarInner}>
          <PersonAvatar person={person} size={112} special={jersey ? null : special} />
        </View>
      </TouchableOpacity>
      <View style={s.texts}>
        <Text style={[s.greeting, { color: tc.text }]} numberOfLines={1}>{t('home.greeting', { name: nickname ?? '' })}</Text>
        {jerseyLabel ? (
          <View style={[s.chip, { backgroundColor: (jersey.color ?? tc.rosa) + '22', borderColor: legibleOn(jersey.color ?? tc.rosa, tc.bg, 1.6) }]}>
            <Text style={[s.chipText, { color: legibleOn(jersey.color ?? tc.rosa, tc.bg, 3) }]} numberOfLines={1}>{jerseyLabel}</Text>
            <Text style={[s.chipRace, { color: tc.textMuted }]} numberOfLines={1}>{jersey.race_name}</Text>
          </View>
        ) : status ? (
          <Text style={[s.status, { color: tc.textMuted }]} numberOfLines={2}>{status}</Text>
        ) : null}
        <ShowcaseRow items={showcase.items} size={30} />
      </View>
    </View>
  )
}

// ─── AvatarInviteCard ────────────────────────────────────────────────
// Invito a personalizzare l'avatar (all'inizio e' quello generato dall'id). Sparisce quando l'utente
// lo chiude (memorizzato sul dispositivo) o quando salva un avatar (avatar_settings.person esiste).
const AVATAR_INVITE_KEY = 'avatar_invite_dismissed_v1'

export function AvatarInviteCard() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const router = useRouter()
  const { avatarSettings } = useAuth()
  const [dismissed, setDismissed] = useState(true) // true finche' non leggo lo storage: niente flash
  useEffect(() => {
    storage.get(AVATAR_INVITE_KEY).then(v => setDismissed(v === '1')).catch(() => setDismissed(false))
  }, [])

  if (dismissed || avatarSettings?.person) return null

  return (
    <View style={[s.invite, { backgroundColor: tc.rosaSubtle, borderColor: tc.rosaBorder }]}>
      <View style={s.inviteTexts}>
        <Text style={[s.inviteTitle, { color: tc.text }]}>{t('avatar.invite_title')}</Text>
        <Text style={[s.inviteBody, { color: tc.textMuted }]}>{t('avatar.invite_body')}</Text>
        <TouchableOpacity onPress={() => router.push('/profilo')} style={[s.cta, { backgroundColor: tc.rosa }]} accessibilityRole="button">
          <Text style={[s.ctaText, { color: tc.brandText }]}>{t('avatar.invite_cta')}</Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity
        onPress={() => { setDismissed(true); storage.set(AVATAR_INVITE_KEY, '1') }}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        style={s.close}
        accessibilityRole="button"
        accessibilityLabel={t('avatar.invite_dismiss')}
      >
        <MaterialCommunityIcons name="close" size={20} color={tc.textMuted} />
      </TouchableOpacity>
    </View>
  )
}

const s = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 20 },
  // Il disegno occupa ~13..98 px dei 112 dell'immagine e testa+spalle stanno nei primi ~80: ritaglio il resto
  avatarBox: { width: 86, height: 80, overflow: 'hidden' },
  // Maglie speciali: la fascia del petto (iridata, europea) e i polsini stanno tra il 71% e l'88% dell'altezza, piu' in basso del ritaglio normale
  avatarBoxSpecial: { height: 100 },
  avatarInner: { marginLeft: -13 },
  texts: { flex: 1, gap: 6 },
  greeting: { fontFamily: fonts.condensedBold, fontSize: 24 },
  status: { fontSize: 13 },
  chip: { alignSelf: 'flex-start', maxWidth: '100%', borderWidth: 1, borderRadius: 10, paddingVertical: 5, paddingHorizontal: 10 },
  chipText: { fontSize: 13, fontWeight: '700' },
  chipRace: { fontSize: 11 },
  invite: { flexDirection: 'row', alignItems: 'flex-start', borderWidth: 1, borderRadius: 16, padding: 14, marginBottom: 16, gap: 8 },
  inviteTexts: { flex: 1 },
  inviteTitle: { fontFamily: fonts.condensedBold, fontSize: 17 },
  inviteBody: { fontSize: 13, marginTop: 2, marginBottom: 10 },
  cta: { alignSelf: 'flex-start', minHeight: 40, paddingHorizontal: 18, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  ctaText: { fontFamily: fonts.condensedBold, fontSize: 14 },
  close: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center', marginTop: -8, marginRight: -8 },
})
